import { z } from "zod";

const emptyToNull = (v: unknown) => (typeof v === "string" && v.trim() === "" ? null : v);

const clientObjectSchema = z.object({
  firstName: z.string().min(2),
  lastName: z.string().min(2),
  // Un email est obligatoire : sans email, le client ne peut ni se créer un
  // compte sur l'espace portail, ni confirmer un rendez-vous.
  email: z
    .string({ required_error: "Email requis" })
    .trim()
    .min(1, "Email requis")
    .email(),
  phone: z.string().optional(),
  specialty: z.string().optional(),
  assignedToId: z.string().optional().nullable(),
  status: z.enum(["ACTIVE", "INACTIVE", "PAUSED"]).default("ACTIVE"),
  /** Pour l’instant seuls les particuliers sont gérés (pas de client entreprise). */
  clientType: z.preprocess(
    (v) => (v === undefined || v === null ? "INDIVIDUAL" : v),
    z.literal("INDIVIDUAL"),
  ),
  companyName: z.preprocess(
    emptyToNull,
    z.string().max(200).nullable().optional(),
  ),
  companySiret: z.preprocess(
    emptyToNull,
    z
      .string()
      .regex(/^\d{14}$/, "Le SIRET doit contenir 14 chiffres")
      .nullable()
      .optional(),
  ),
  companyVatNumber: z.preprocess(
    emptyToNull,
    z.string().max(20).nullable().optional(),
  ),
  address: z.preprocess(
    emptyToNull,
    z.string().max(500).nullable().optional(),
  ),
});

const rejectCompanyFields = (
  data: {
    companyName?: string | null;
    companySiret?: string | null;
    companyVatNumber?: string | null;
  },
  ctx: z.RefinementCtx,
) => {
  const has =
    (typeof data.companyName === "string" && data.companyName.trim() !== "") ||
    (typeof data.companySiret === "string" && data.companySiret.trim() !== "") ||
    (typeof data.companyVatNumber === "string" &&
      data.companyVatNumber.trim() !== "");
  if (!has) return;
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message:
      "Les clients entreprise ne sont pas pris en charge pour le moment.",
    path: ["companyName"],
  });
};

export const ClientSchema = clientObjectSchema.superRefine(rejectCompanyFields);

/** Champs partiels pour `PUT /clients/:id` — rejette toute donnée « entreprise ». */
export const ClientUpdateSchema = clientObjectSchema
  .partial()
  .extend({
    clientType: z.literal("INDIVIDUAL").optional(),
  })
  .superRefine(rejectCompanyFields);

export type ClientDto = z.infer<typeof ClientSchema>;
export type ClientUpdateDto = z.infer<typeof ClientUpdateSchema>;
export type ClientCreateInput = z.input<typeof ClientSchema>;
export type ClientUpdateInput = z.input<typeof ClientUpdateSchema>;
